/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";
import { ObjectData } from "../objects-selector/helpers";

/* import { renderListItems } from "../../../common/render-list-item"; */

@Component({
  tag: "gx-ide-bpm-export-xpdl",
  styleUrl: "bpm-export-xpdl.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-export-xpdl"]
})
export class GxIdeBpmExportXpdl {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmExportXpdlElement;
  private exportInputEl: HTMLInputElement;
  private bpmObjectSelectorEl: HTMLGxIdeBpmObjectsSelectorElement;

  // 3.STATE() VARIABLES //

  @State() emptyInput = true;
  @State() emptyObjects = true;
  @State() notExporting = true;

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Callback function invoked when the user presses the 'Create' button.
   * This function is expected to handle the export process based on the provided export data.
   *
   * @param {ExportData} exportData - The data required for the export process.
   * @returns {Promise<boolean>} A promise that resolves to a boolean indicating the success of the export operation.
   * Returns `true` if the export was successful, and `false` otherwise.
   */
  @Prop() readonly exportCallback: ExportCallback;

  /**
   * Callback invoked when user press 'Cancel' button
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Callback invoked when the user presses the 'Add' button. associated with gx-ide-bpm-objects-selector
   * This function is expected to return a list of selected objects' basic information.
   * @returns {Promise<ObjectData[]>} A Promise that resolves to an array of ObjectData. Each ObjectData contains the ID, icon, and name of an object.
   */
  @Prop() readonly addCallback: () => Promise<ObjectData[]>;

  /**
   * Callback invoked when the user presses the 'References' button. associated with gx-ide-bpm-objects-selector
   * Receives a parameter which is an array of IDs of the objects currently added to the list.
   * Returns a Promise that resolves to an array of ObjectData, where each ObjectData contains the ID, icon, and name of an object.
   * @param {string[]} ids - An array of IDs of the objects currently added to the list.
   * @returns {Promise<ObjectData[]>} A Promise that resolves to an array of ObjectData. Each ObjectData contains the ID, icon, and name of an object.
   */
  @Prop() readonly referencesCallback: (ids: string[]) => Promise<ObjectData[]>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private exportHandler = () => {
    if (this.exportCallback) {
      this.notExporting = false;

      const data: ExportData = {
        directoryName: this.exportInputEl.value,
        Objects: this.bpmObjectSelectorEl.selection ?? {
          includeAll: false,
          objectIds: []
        }
      };

      this.exportCallback(data)
        .then(() => {
          this.notExporting = true;
        })
        .catch(() => {
          // error handling
          this.notExporting = true;
        });
    }
  };

  private inputHandle = async () => {
    this.emptyInput = !this.exportInputEl.value?.length;
  };

  private handleSelectionChanged = () => {
    this.emptyObjects = !this.bpmObjectSelectorEl.selection.objectIds.length;
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <div class="header">
              <label htmlFor="export-directory" class="label">
                {this._componentLocale.main.exportInputLabel}
              </label>
              <input
                type="text"
                id="export-directory"
                class="input-text input"
                onInput={this.inputHandle}
                ref={el => (this.exportInputEl = el as HTMLInputElement)}
              />
              <button
                class="export"
                disabled={this.emptyInput || this.emptyObjects}
                onClick={this.exportHandler}
              >
                {this._componentLocale.main.exportButton}
              </button>
              <button
                class="cancel"
                disabled={this.notExporting}
                onClick={this.cancelCallback}
              >
                {this._componentLocale.main.cancelButton}
              </button>
            </div>

            <gx-ide-bpm-objects-selector
              id="gx-ide-bpm-objects-selector"
              addCallback={this.addCallback}
              referencesCallback={this.referencesCallback}
              ref={el =>
                (this.bpmObjectSelectorEl =
                  el as HTMLGxIdeBpmObjectsSelectorElement)
              }
              onSelectionChanged={this.handleSelectionChanged}
            ></gx-ide-bpm-objects-selector>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type ExportCallback = (exportData: ExportData) => Promise<boolean>;

export type CancelCallback = () => Promise<void>;

export type ExportData = {
  directoryName: string;
  Objects: SelectionData;
};

export type SelectionData = {
  includeAll: boolean;
  objectIds: string[];
};
